import React, { useState, useEffect } from 'react';
import { useEscapeClose } from '../../../utils/SamuiUtils';

const ApModal = ({ showApModal, handleApClose, apDataList, onRowSelectAp }) => {
    // สถานะการค้นหา
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredApDataList, setFilteredApDataList] = useState(apDataList);
    const [selectedRowIndex, setSelectedRowIndex] = useState(0); // เพิ่ม state สำหรับเลือกแถว

    useEscapeClose(showApModal, handleApClose);

    // ฟิลเตอร์ข้อมูลเมื่อ searchTerm หรือ apDataList เปลี่ยนแปลง
    useEffect(() => {
        setFilteredApDataList(
            apDataList.filter(ap =>
                ap.AP_Code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ap.AP_Name.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [searchTerm, apDataList]);

    useEffect(() => {
        if (showApModal) {
            setSearchTerm('');
            setSelectedRowIndex(0); // รีเซ็ตการเลือกแถวเมื่อเปิด modal
        }
    }, [showApModal]);

    // เพิ่ม keyboard navigation สำหรับ modal
    useEffect(() => {
        const handleKeyDown = (event) => {
            // ทำงานเฉพาะเมื่อ modal เปิดอยู่
            if (!showApModal) return;

            // ป้องกันการทำงานถ้า focus อยู่ใน input search
            if (document.activeElement.tagName === 'INPUT') {
                // ยกเว้น Enter, ESC, F5 และ ArrowDown ที่ยังให้ทำงานได้ใน input
                if (event.key !== 'Enter' && event.key !== 'Escape' && event.key !== 'F5' && event.key !== 'ArrowDown') {
                    return;
                }
            }

            if (filteredApDataList.length === 0) return;

            switch (event.key) {
                case 'ArrowUp':
                    event.preventDefault();
                    setSelectedRowIndex(prevIndex => {
                        const newIndex = prevIndex > 0 ? prevIndex - 1 : filteredApDataList.length - 1;
                        // เลื่อน scroll ไปยังแถวที่เลือก
                        setTimeout(() => scrollToRow(newIndex), 0);
                        return newIndex;
                    });
                    break;

                case 'ArrowDown':
                    event.preventDefault();
                    // ถ้าอยู่ใน input และกดลูกศรลง ให้ focus ไปที่ตารางและเลือกแถวแรก
                    if (document.activeElement.tagName === 'INPUT') {
                        setSelectedRowIndex(0);
                        setTimeout(() => scrollToRow(0), 0);
                        // เอา focus ออกจาก input
                        document.activeElement.blur();
                        return;
                    }
                    
                    setSelectedRowIndex(prevIndex => {
                        const newIndex = prevIndex < filteredApDataList.length - 1 ? prevIndex + 1 : 0;
                        // เลื่อน scroll ไปยังแถวที่เลือก
                        setTimeout(() => scrollToRow(newIndex), 0);
                        return newIndex;
                    });
                    break;

                case 'Enter':
                    event.preventDefault();
                    if (filteredApDataList[selectedRowIndex]) {
                        onRowSelectAp(filteredApDataList[selectedRowIndex]);
                    }
                    break;

                case 'Escape':
                    event.preventDefault();
                    event.stopPropagation(); // ป้องกันไม่ให้ event ไปยัง parent elements
                    event.stopImmediatePropagation(); // ป้องกันไม่ให้ listener อื่นทำงาน
                    console.log('ESC pressed in ApModal, closing modal');
                    handleApClose();
                    return false; // ป้องกันการ bubble ขึ้นไป
                    break;

                case 'Home':
                    event.preventDefault();
                    setSelectedRowIndex(0);
                    setTimeout(() => scrollToRow(0), 0);
                    break;

                case 'F5':
                    event.preventDefault();
                    const searchInput = document.querySelector('.modal.show input[type="text"]');
                    
                    // ถ้า focus อยู่ใน input อยู่แล้ว ให้เอา focus ออกเพื่อใช้ลูกศรได้
                    if (document.activeElement.tagName === 'INPUT') {
                        document.activeElement.blur();
                        setSelectedRowIndex(0); // เลือกแถวแรก
                        setTimeout(() => scrollToRow(0), 0);
                    } else {
                        // ถ้าไม่ได้ focus ใน input ให้ focus ช่องค้นหา
                        if (searchInput) {
                            searchInput.focus();
                        }
                    }
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown, true); // ใช้ capture phase เพื่อให้ทำงานก่อน

        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [showApModal, filteredApDataList, selectedRowIndex, onRowSelectAp, handleApClose]);

    // ฟังก์ชันสำหรับเลื่อน scroll ไปยังแถวที่เลือก
    const scrollToRow = (rowIndex) => {
        const tableContainer = document.querySelector('.table-responsive');
        const selectedRow = document.querySelector(`tbody tr:nth-child(${rowIndex + 1})`);
        
        if (tableContainer && selectedRow) {
            const containerRect = tableContainer.getBoundingClientRect();
            const rowRect = selectedRow.getBoundingClientRect();
            
            // คำนวณตำแหน่งที่ต้องเลื่อน
            const rowTop = selectedRow.offsetTop;
            const rowHeight = selectedRow.offsetHeight;
            const containerHeight = tableContainer.clientHeight;
            const scrollTop = tableContainer.scrollTop;
            
            // ตรวจสอบว่าแถวอยู่นอกเขตมองเห็นหรือไม่
            if (rowTop < scrollTop) {
                // แถวอยู่เหนือเขตมองเห็น - เลื่อนขึ้น
                tableContainer.scrollTop = rowTop - 10; // เผื่อ padding
            } else if (rowTop + rowHeight > scrollTop + containerHeight) {
                // แถวอยู่ใต้เขตมองเห็น - เลื่อนลง
                tableContainer.scrollTop = rowTop + rowHeight - containerHeight + 10; // เผื่อ padding
            }
        }
    };

    // รีเซ็ต selectedRowIndex เมื่อ filteredApDataList เปลี่ยน
    useEffect(() => {
        setSelectedRowIndex(0);
    }, [filteredApDataList]);

    return (
        <>
            <div className={`modal ${showApModal ? 'show' : ''}`} style={{ display: showApModal ? 'block' : 'none' }} tabIndex="-1" role="dialog">
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">รายชื่อผู้ขาย</h5>
                            <button type="button" className="close" onClick={handleApClose}>
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="ค้นหา รหัสผู้ขาย หรือ ชื่อผู้ขาย"
                                    value={searchTerm || ''}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="table-responsive" style={{ maxHeight: '650px', overflowY: 'auto' }}>
                                <table className="table table-striped table-hover">
                                    <thead className="thead-dark">
                                        <tr>
                                            <th className="text-center" style={{ width: '139px' }}>รหัสผู้ขาย</th>
                                            <th className="text-center" style={{ width: '620px' }}>ชื่อผู้ขาย</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredApDataList && filteredApDataList.length > 0 ? (
                                            filteredApDataList.map((ap, index) => (
                                                <tr
                                                    key={ap.AP_Id || index + 1}
                                                    onClick={() => {
                                                        setSelectedRowIndex(index);
                                                        onRowSelectAp(ap);
                                                    }}
                                                    style={{ 
                                                        cursor: 'pointer',
                                                        backgroundColor: selectedRowIndex === index ? 'rgb(105, 188, 216)' : 'transparent',
                                                        transition: 'background-color 0.3s ease'
                                                    }}>
                                                    <td className="text-center">{ap.AP_Code}</td>
                                                    <td>{ap.AP_Name}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="10">
                                                    <center>
                                                        <h5>ไม่พบข้อมูล</h5>
                                                    </center>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <small className="text-muted me-auto">
                                <i className="fas fa-keyboard me-1"></i>
                                <strong>คีย์ลัด:</strong> ↑↓ เลือกแถว | Enter เลือก | ESC ปิด
                            </small>
                            <button className="btn btn-secondary" onClick={handleApClose}>
                                ปิด (ESC)
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {/* Overlay */}
            {showApModal && <div className="modal-backdrop fade show"></div>}
        </>
    );
};

export default ApModal;
